import {useNavigate, useParams} from "react-router-dom";
import {Autocomplete, Box, Divider, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {PrintTemplateService} from "crqlar-react-common/dist/services/template/printTemplate.service";
import {PrintTemplate, PrintTemplateType} from "crqlar-react-common/dist/models/template/printTemplate.model";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";

const PrintTemplateDetails = () => {

    //region State

    useTrackScreenVisit('system print template details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const templateData = useEntity({
        get: async () => id && id !== 'new' ? PrintTemplateService.getTemplateById(id) : {},
        create: PrintTemplateService.createTemplate,
        update: PrintTemplateService.updateTemplate,
        delete: (id) => PrintTemplateService.deleteTemplate({id: [id]}),
        onDeleted: () => navigate('/system/printTemplates', {replace: true}),
        onSaved: (template, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/system/printTemplates/${template.id}`, {replace: true})
            }
        },
        isValid: (template) => !!template.name?.trim(),
        cleanupBeforeSave: (template) => ({
            ...template,
            name: template.name?.trim()
        })
    }, id)

    const template = templateData.item
    const setTemplate = templateData.setItem
    const nameMissing = isRequiredTextMissing(templateData, template.name)

    const hasPrintConfiguration =
        template.type === PrintTemplateType.RESERVATION_CONFIRMATION ||
        template.type === PrintTemplateType.FOLIO_CONFIRMATION

    //endregion State

    //region Handlers

    const updateField = (field: keyof PrintTemplate, value: any) => {
        setTemplate({...template, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={template.name}
            data={templateData}
            back={() => navigate('/system/printTemplates')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Internal label for finding and managing this print template.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={template.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Type')}
                    tooltip={t('Determines which print workflow uses this template and which data is available.')}
                >
                    <Autocomplete
                        id={'template-type'}
                        options={Object.values(PrintTemplateType)}
                        getOptionLabel={(option: PrintTemplateType) => option ? t(`Print template type ${option}`) : t('(Not set)')}
                        value={template.type || null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        onChange={(e, value) => updateField('type', value)}
                        fullWidth
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const printConfigurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Print Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Show signature')}
                    tooltip={t('Adds a signature area to supported confirmation templates.')}
                >
                    <Switch
                        id={'show-signature'}
                        checked={!!template.showSignature}
                        onChange={(e) => updateField('showSignature', e.target.checked)}
                        sx={{ml: 'auto'}}
                    />
                </Property>

                <Property
                    title={t('Show tip')}
                    tooltip={t('Adds a tip line to supported folio or confirmation templates.')}
                >
                    <Switch
                        id={'show-tip'}
                        checked={!!template.showTip}
                        onChange={(e) => updateField('showTip', e.target.checked)}
                        sx={{ml: 'auto'}}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const contentSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Content')}
            </Typography>

            <Stack gap={4}>
                <Stack gap={2}>
                    <Typography variant={'h6'} textAlign={'start'}>
                        {t('Banner image')}
                    </Typography>

                    <ImageUploadField
                        id={'image-upload-header-logo'}
                        image={template.headerLogo}
                        setImage={(image) => updateField('headerLogo', image)}
                        ratio={'landscape'}
                    />
                </Stack>

                <Stack gap={2}>
                    <Typography variant={'h6'} textAlign={'start'}>
                        {t('Text content')}
                    </Typography>

                    <Stack gap={1}>
                        <Property
                            title={t('Header content 1')}
                            tooltip={t('Localized text block shown near the top of the printed template.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'header-content-1-localized'}
                                size={'small'}
                                value={template.headerContent1Localized}
                                onChange={(e) => updateField('headerContent1Localized', e)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Property>

                        <Property
                            title={t('Header content 2')}
                            tooltip={t('Second localized text block shown near the top of the printed template.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'header-content-2-localized'}
                                size={'small'}
                                value={template.headerContent2Localized}
                                onChange={(e) => updateField('headerContent2Localized', e)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Property>

                        <Property
                            title={t('Bottom content 1')}
                            tooltip={t('Localized text block shown near the bottom of the printed template.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'bottom-content-1-localized'}
                                size={'small'}
                                value={template.bottomContent1Localized}
                                onChange={(e) => updateField('bottomContent1Localized', e)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Property>

                        <Property
                            title={t('Bottom content 2')}
                            tooltip={t('Second localized text block shown near the bottom of the printed template.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'bottom-content-2-localized'}
                                size={'small'}
                                value={template.bottomContent2Localized}
                                onChange={(e) => updateField('bottomContent2Localized', e)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Property>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete print template')}
                description={t('This permanently deletes the print template. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={templateData.saving}
                onDelete={() => templateData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {template.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {basicInfoSection}
                <Divider/>
                {contentSection}

                {
                    hasPrintConfiguration &&
                    <>
                        <Divider/>
                        {printConfigurationSection}
                    </>
                }

                {
                    !!template.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    )
}

export default PrintTemplateDetails;
